import { DatePipe } from '@angular/common';
import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { ChartConfiguration } from 'chart.js';
import moment from 'moment';
import { BaseChartDirective } from 'ng2-charts';
import { Subscription } from 'rxjs';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { AgContextMenuService } from 'src/app/reusables/services/ag-context-menu.service';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleHelperService } from 'src/app/reusables/services/module-helper.service';
import { ModuleThemesService } from 'src/app/reusables/services/module-themes.service';
import SimpleContextMenuRenderer from 'src/shared/ag-grid-components/simple-context-menu';
import { ModuleManager } from 'src/shared/models/module-manager';
import { IModuleManagerMenuItem, ModuleManagerMenuItemType, ModuleManagerSubMenu } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { ModuleManagerMetricInfo } from 'src/shared/models/module-manager/module-manager-settings.models';
import { AgentStateDetailComponent } from '../../modals/agent-state-detail/agent-state-detail.component';
import { TasksModalComponent } from '../../modals/tasks-modal/tasks-modal.component';
import { WidgetThresholdsModalComponent } from '../../modals/widget-thresholds-modal/widget-thresholds-modal.component';
import { ThresholdItem } from '../../modals/widget-thresholds-modal/widget-thresholds-modal.model';
import { WidgetThresholdsModalService } from '../../modals/widget-thresholds-modal/widget-thresholds-modal.service';
import { CurrentAgentStatesService } from '../current-agent-states/current-agent-states.service';
import { ModuleView, PerformanceModuleData, PerformanceModuleDataRow } from './summary-combo.models';
import { SummaryComboService } from './summary-combo.service';
import { ObjectivesDetailsService } from '../../../reusables/objectives-details/objectives-details.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ErrorCode } from 'src/shared/models/error-code.model';
import { NumericType, TimeType } from '../../../../shared/models/metric';
import { CxoneAwardModalComponent } from '../../modals/cxone-award-modal/cxone-award-modal.component';
import { AgentStateDetailCXOneComponent } from '../../modals/agent-state-detail-cxone/agent-state-detail-cxone.component';
import { SuggestCoachingModalComponent } from '../../modals/suggest-coaching-modal/suggest-coaching-modal.component';
import { ContextMenuAwardsDialogComponent } from '../../awards/context-menu-awards-dialog/context-menu-awards-dialog.component';

@Component({
    selector: 'cv-summary-combo',
    templateUrl: './summary-combo.component.html',
    styleUrls: ['./summary-combo.component.scss']
})

export class SummaryComboComponent implements OnInit, OnDestroy {
    @Input() public moduleManager: ModuleManager;
    @Input() moduleKey: string;
    @Input() metrics: ModuleManagerMetricInfo[] = [];
    @ViewChild("searchBox", { read: ElementRef }) searchBox: ElementRef<HTMLInputElement>;
    @ViewChild(BaseChartDirective) chartObject: BaseChartDirective;
    @ViewChild('summaryWidgetGrid') agGrid: AgGridAngular;

    // used in controller (cant get in the controller since the same component and same service gets used in two different widgets)
    moduleID: number;
    isFirstLoad = true;
    isAborting = false;
    data: PerformanceModuleData = null;
    dataLoading = false;
    dataLoadPromise = null;
    abortLoad: () => void = null;
    dataLoadSuccess = false;
    isManualRefresh = false;
    initialSettingsApplied = false;
    firstLoad = true;
    isNoMetric = false;
    isDateRangeTooWideIntraday = false;
    localizationResult = null;
    subscriptions: Subscription[] = [];
    tooltipLegendItems = new Map<number, string>();
    isAgentStateDetailModalOpen = false;

    //For cxone search placeholder localization
    searchPlaceholder = "";

    //View helpers
    singleMode = true;
    dualMode = false;

    //State Management
    moduleState = {
        // General State Settings
        name: 'performanceComboState',
        autoLoad: true,
        initialLoad: false,

        // Combo Module States
        view: ModuleView.Table,

        // this refers to the charts state
        graphState: {
            chartType: 'bar',
            legendPosition: 'top',
            axisShowStates: {},
            themeIndex: 0,
            showLegendPosition: true
        },
    };

    backgroundcolor = {
        id: 'custom_canvas_background_color',
        beforeDraw: (chart) => {
            const ctx = chart.canvas.getContext('2d');
            ctx.save();
            ctx.globalCompositeOperation = 'destination-over';
            ctx.fillStyle = '#F5F8FA';
            ctx.fillRect(0, 0, chart.canvas.width, chart.canvas.height);
            ctx.restore();
        }
    };

    //
    // for chart component
    //
    preventLoadingModal = false; // Set if data is reloading via auto refresh
    themeIndex = (this.moduleState.graphState.themeIndex != null) ? this.moduleState.graphState.themeIndex : 0;
    chartType = (this.moduleState.graphState.chartType != null) ? this.moduleState.graphState.chartType : 'bar';
    chartLegendPosition: string;
    hideShowLegendClicked = false;
    legendItemsHidden = new Map();
    themeMenuOptions = [];
    chartConfig = null;
    chartPadding = { //Chart.js defaults all padding values to 0.
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
    };
    zoom = {
        max: 2.5,
        min: 1,
        cur: 1,
        baseTicks: 5,
    };
    chartAxisShowStates = {};
    currentTheme = null;
    modalRef: NgbModalRef = null;

    ///
    /// For Table Component
    ///
    filterInput = null; // This is the <input> element for the search bar. Set in the link function.  When search is opened, focus is set to it.
    footerRow = {};
    avgFooterRow = {};
    filterValue = "";
    searchOpen = false; // starting out closed, first run through will switch to false
    tableState = null; // Object used to store the table state
    showTotal = true;
    showFooter = true; // Set if the display option is checked in the menu
    columnHeaderAlignments = {};
    gridReady = false;
    parentVisible = false;
    agentInfoVisible = false;
    stateSaveTimer = null;
    params = null;
    firstAnimateComplete = false;
    noData = false;
    public gridOptions = null;
    tableDisplayItems = [];
    displayCheckItems = [];
    isCXOne = false;
    private metricThresholds: Record<string, ThresholdItem[]> = {};
    private metricSort: string = null;
    private groupSort: string = null;
    private sortID = 0;

    //Object for ag grid localization
    localeText = {};

    // Boolean to check if the widget is shared via URL
    isSharedViaURL = window.location.pathname.includes('SharedDashboard.aspx');

    private stateRestoreInProgress = false; // Flag to prevent state save while restoring state until the widget is "stable"

    constructor(
        private service: SummaryComboService,
        private cvLocalizationService: CVLocalizationService,
        private agGridHelperService: AgGridHelperService,
        private moduleThemesService: ModuleThemesService,
        public config: CvConfigurationService,
        private agContextMenuService: AgContextMenuService,
        private agentStateService: CurrentAgentStatesService,
        private navigationService: NavigationService,
        private ngbModal: NgbModal,
        private datePipe: DatePipe,
        private thresholdService: WidgetThresholdsModalService,
        private moduleHelperService: ModuleHelperService,
        private objectivesDetailsService: ObjectivesDetailsService,
    ) {
        this.isCXOne = config.isCxOne;
    }

    get isResizeWaiting() { return this.moduleManager?.container.isResizeWaiting; }

    openAgentStateDetail(moduleKey: string, groupID: number, selectedRow: any) {
        if(this.isAgentStateDetailModalOpen) return;
        this.isAgentStateDetailModalOpen = true;
        this.navigationService.showOverlay = true;

        this.modalRef = this.isCXOne
            ? this.ngbModal.open(AgentStateDetailCXOneComponent,
                { size: "xl", centered: true, scrollable: true, modalDialogClass: 'cx-one-ui' })
            : this.ngbModal.open(AgentStateDetailComponent,
                { size: "xl", centered: true, scrollable: true, modalDialogClass: null });
        this.modalRef.result.finally(() => {
            this.navigationService.showOverlay = false;
            this.isAgentStateDetailModalOpen = false;
        });

        const component = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
    }

    openCreateTaskModal(moduleKey: string, groupID: number, selectedRow: any) {
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(TasksModalComponent,
            { size: "xl", centered: true, scrollable: true, modalDialogClass: null });
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
        const component: TasksModalComponent = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
    }

    openThresholdsModal(moduleKey: string) {
        this.metrics = this.moduleManager.moduleSettings.selectedMetrics;
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(WidgetThresholdsModalComponent,
            { size: "med", centered: true, scrollable: true, modalDialogClass: this.config.isCxOne ? 'cx-one-ui' : null });
        this.modalRef.result.then(
            () => {
                this.metricThresholds = this.modalRef.componentInstance.metricThresholds;
                this.moduleManager.moduleOptions.onRefresh(false);
                this.moduleManager.requestPublishNeeded();
            },
            () => { }
        );
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
        const component: WidgetThresholdsModalComponent = this.modalRef.componentInstance;
        component.metrics = this.moduleManager.moduleSettings.selectedMetrics;
        component.moduleKey = moduleKey;
    }

    openAwardModal(moduleKey: string, groupID: number, selectedRow: any, isCxone: boolean) {
        this.navigationService.showOverlay = true;
        let component: any;
    
        if (isCxone) {
            this.modalRef = this.ngbModal.open(CxoneAwardModalComponent,
                { size: "xl", centered: true, scrollable: true, modalDialogClass: 'give-award-dialog' });
            component = this.modalRef.componentInstance;
        } else {
            this.modalRef = this.ngbModal.open(ContextMenuAwardsDialogComponent,
                { size: "xl", centered: true, scrollable: true, modalDialogClass: 'give-award-dialog' });
            component = this.modalRef.componentInstance;
        }
    
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
    
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
    }

    openSuggestCoachingModal(moduleKey: string, groupID: number, selectedRow: any) {
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(SuggestCoachingModalComponent,
            { size: "md", centered: true, scrollable: true, modalDialogClass: null });
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
        const component: SuggestCoachingModalComponent = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
    }

    async loadLocalizations() {
        this.localeText = await this.agGridHelperService.getLocalizations();
        const localizationPromises = [
            this.cvLocalizationService.GetSection("Common"),
            this.cvLocalizationService.GetSection("Error_Module"),
            this.cvLocalizationService.GetSection("Summary_Module"),
            this.cvLocalizationService.GetSection("Objectives"),
        ];
        await Promise.all(localizationPromises);
        this.searchPlaceholder = this.cvLocalizationService.getCachedText("Common", "Search", "Search");

        this.tableDisplayItems = [ // items for the display menu that uses both radio buttons and checkboxes
            {
                displayName: this.getLocaleText('Show_Totals'),
                inputType: 'radio',
                value: true,
                checked: this.showTotal
            },
            {
                displayName: this.cvLocalizationService.getCachedText("Common", "None", "None"),
                inputType: 'radio',
                value: false,
                checked: this.showTotal
            },
            {
                displayName: "",
                inputType: 'divider',
            },
            {
                displayName: this.getLocaleText('Show_Parent_Level'),
                inputType: 'checkbox',
                checked: this.parentVisible
            },
            {
                displayName: this.getLocaleText('Show_Agent_Info'),
                inputType: 'checkbox',
                checked: this.agentInfoVisible
            }
        ];

        this.displayCheckItems = [ //check items for the table display, cxone wont have avgs
            {
                displayName: this.getLocaleText('Show_Totals'),
                checked: true
            },
            {
                displayName: this.getLocaleText('Show_Parent_Level'),
                checked: this.parentVisible
            },
            {
                displayName: this.getLocaleText('Show_Agent_Info'),
                checked: this.agentInfoVisible
            }
        ];
    }

    async ngOnInit() {

        this.moduleID = this.moduleManager.container.moduleInfo.DTO?.ModuleID;
        this.moduleManager.showLoader = true; //show loader on init for subscription preview

        this.themeMenuOptions = this.moduleThemesService.GetModuleThemesMenu();
        this.setThemeClick();
        await this.loadLocalizations();

        this.createDefaultGrid();

        this.moduleManager.setOptions({
            onSettingsApplied: async (settings) => {
                // If the selectedMetrics aren't loaded in yet, no need to make a call to loadData.
                if (!settings?.selectedMetrics?.length && this.moduleManager.getModuleInfo().ModuleID !== 112) {
                    this.isNoMetric = true;
                    this.moduleManager.showLoader = false;
                    this.moduleManager.container.showDateRange = true;
                    return;
                }
                this.moduleState.initialLoad = true;
                await this.loadData(false);
                if (this.agGrid?.api) this.agGrid?.api.refreshHeader();
                this.populateData();
            },
            hasRefresh: true,
            onImageExport: (isCXOne) => this.exportPNG(),
            imageExportVisible: () => this.moduleState.view !== ModuleView.Table,
            onRefresh: async isAutoRefresh => {
                await this.loadData(isAutoRefresh);
                this.populateData();
            },
            canRefresh: () => !this.dataLoading,
            onExport: (format, isCXOne) => this.service.tableLoadPerformanceDataForExport(this.moduleKey, format, isCXOne, this.moduleID),
        });

        this.moduleManager.addState({
            name: "tableState",
            getStateData: () => this.tableState,
            onStateLoaded: stateData => {
                if (!stateData) { // No table state loaded
                    return;
                }
                this.tableState = stateData;
                this.restoreColumnState();
                this.setShowParentStatus()
                //this.onTableStateLoaded(this.tableState);

                //this checks for the groupsort
                if(Object.keys(stateData.columns[0].sort).length > 0){
                    this.groupSort = stateData.columns[0].sort.direction;
                }

                for(let i = 2; i < stateData.columns.length; i++){
                    if(Object.keys(stateData.columns[i].sort).length > 0){
                        this.metricSort = stateData.columns[i].sort.direction;
                        this.sortID = Number(stateData.columns[i].name.replace('Metric', ''));
                    }
                }
            },
            autoLoad: true
        });

        this.moduleManager.addState({
            name: "footerVisible",
            getStateData: () => this.isCXOne ? this.showFooter : this.showTotal,
            onStateLoaded: stateData => {
                if (typeof stateData !== 'boolean') {
                    return;
                }
                //this.hideTotal = stateData;
                if (this.isCXOne) {
                    this.showFooter = stateData;
                    this.displayCheckItems[0].checked = this.showFooter;
                    this.setFooterVisible(this.showFooter);
                }
                else {
                    this.showTotal = stateData;
                    this.tableDisplayItems.filter(i => i.inputType === 'radio').forEach((item) => { item.checked = this.showTotal });
                    this.setFooterVisible(this.showTotal);
                }    
            },
            autoLoad: true
        });

        this.moduleManager.addState({
            name: 'performanceComboState',
            autoLoad: true,
            getStateData: () => this.moduleState,
            onStateLoaded: stateData => {
                if (!stateData) { // if no data make an empty table
                    this.setModuleState(ModuleView.Table);
                    return;
                }
                // set data and theme info when state loads
                this.moduleState = stateData;
                if (this.moduleState.view === 'graph') {
                    this.moduleState.view = ModuleView.Chart;
                }
                if (this.moduleState.view !== 'table') {

                    this.chartType = this.moduleState.graphState.chartType || 'bar';
                    this.themeIndex = (this.moduleState.graphState.themeIndex != null) ? this.moduleState.graphState.themeIndex : 0;
                }

                this.currentTheme = this.moduleThemesService.ModuleThemes[this.themeIndex].Colors;

                if (this.moduleState.view) { // if a view has been set set the state to the view
                    this.setModuleState(this.moduleState.view);
                } else { // else default to table
                    this.setModuleState(ModuleView.Table);
                }
                this.moduleState.initialLoad = true;
            }
        });

        if (this.moduleState.graphState === null) {
            // this.moduleState.graphState.themeIndex = (this.moduleState.graphState.themeIndex != null) ?
            //         this.moduleState.graphState.themeIndex : 0
            this.chartType = (this.moduleState.graphState.chartType !== null) ? this.moduleState.graphState.chartType : 'bar';
            this.chartLegendPosition = (this.moduleState.graphState.legendPosition !== null) ?
                this.moduleState.graphState.legendPosition : 'top';
        }

        if (!this.moduleManager.hasWriteAccess() && !this.isCXOne) {
            this.agGridHelperService.setGridReadOnly(this.gridOptions);
        }

        // Parent
        if (this.gridOptions.columnDefs) {
            this.gridOptions.columnDefs.forEach((el: ColDef) => {
                if (el.field === "parentGroupData") {
                    this.parentVisible = !el.hide;
                    return;
                }
                if (el.field.startsWith('AAgent')) {
                    this.agentInfoVisible = !el.hide;
                    return;
                }
            });
        }

        this.resetMenuItems();
        this.metrics = this.moduleManager.moduleSettings.selectedMetrics;
        this.setComboMenu(); //Ensures the Widget Display menu is available on the first load.
    }

    createDefaultGrid() {
        // grid options for agGrid
        this.gridOptions = this.agGridHelperService.createGridOptions({
            context: { moduleManager: this.moduleManager },
            components: {
                groupRenderer: this.agGridHelperService.GroupDrillDownCellRenderer,
                groupHeaderComponent: this.agGridHelperService.GroupHeaderComp,
                metricMenuRenderer: SimpleContextMenuRenderer,
            },

            // grid options
            accentedSort: true,
            animateRows: true,
            suppressScrollOnNewData: true,
            suppressContextMenu: true,
            tooltipInteraction: true,

            // grid data
            defaultColDef: {
                minWidth: this.agGridHelperService.defaultMinWidth,
                enableCellChangeFlash: false,
            },
            columnDefs: [],
            rowData: [],

            // grid events
            onGridReady: event => {
                this.gridReady = true;

                this.stateRestoreInProgress = true; // Prevent saving while the widget is still loading the initial states.

                this.restoreColumnState(); //Needed to restore columns size saved on the tablestate.
                if (!this.tableState) {
                    this.agGridHelperService.sizeColumnsSmart(event); // Timing: This happens before events are fired.
                    this.tableState = this.agGridHelperService.getGridState(this.agGrid.api); // This does too.
                    setTimeout(async () => await this.moduleManager.saveState('tableState'), 1); // This fires AFTER events to silently save when the grid isn't initialized.
                }

                setTimeout(() => this.stateRestoreInProgress = false, 0); // Wait for the next tick to let events finish before allowing state save.
            },
            getRowId: ({ data }) => data.Group.groupID,
            onSortChanged: event => {
                this.tableStateChanged();
                this.metricSort = null;
                this.groupSort = null;
                this.sortID = 0;

                let sortModel = event.columnApi.getColumns().map(col => ({
                    colId: col.getColId(),
                    sort: col.getSort()
                })).filter(col => col.sort !== null);

                if (sortModel.length >= 3) {
                    for(let i = 2; i < sortModel.length; i++) {
                        if(sortModel[i].sort != undefined){
                            this.metricSort = sortModel[i].sort;
                            this.sortID = this.data.columnDefs[i].metricID;
                        }
                    }
                }
                this.groupSort = sortModel[0].sort;

                if(this.moduleState.view != 'table'){
                    this.updateChartLabelandData();
                }
            },
            onColumnMoved: () => this.tableStateChanged(),
            onColumnPinned: () => this.tableStateChanged(),
            onColumnVisible: () => this.tableStateChanged(),
            onGridSizeChanged: event => {
                if (this.isResizeWaiting) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                    this.tableStateChanged();
                } else if (this.moduleManager.container.isFullscreen) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                } else {
                    this.restoreColumnState();
                }
            },
            onColumnResized: event => {
                if (event.source === "uiColumnResized" || (event.source == "autosizeColumns" && !this.moduleManager.container.isTogglingFullscreen)) {
                    this.tableStateChanged();
                }
            },
            overlayNoRowsTemplate: this.config.isCxOne ?
                `<div class="error-container" style="padding-top: 48px;">
                    <svg class="mb-2">
                        <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
                    </svg>
                    <p class="cxone-error-text">
                        ${this.cvLocalizationService.getCachedText("Error_Module", "CXOne_No_Results", "No results found")}
                    </p>
                </div>` : null,
            onFilterChanged: () => this.tableStateChanged(),
            onCellClicked: params => this.params = params,
            getContextMenuItems: params => this.getContextMenuItem(params),
            suppressDragLeaveHidesColumns: true,
            suppressNoRowsOverlay: true,
            localeText: this.localeText,
        }, {
            enableCopy: true
        });

        this.subscriptions.push(this.agContextMenuService.agentDetailsClicked.subscribe(async params => {
            // if multiple widgets are opened that support this option we need to assure only the clicked one opens the modal
            if (params.moduleKey !== this.moduleManager.getModuleKey()) {
                return;
            }
            const response = await this.agentStateService.loadCurrentAgentStatesByGroupID(params.moduleKey, params.groupData.groupID, this.isCXOne);
            this.openAgentStateDetail(params.moduleKey, params.groupData.groupID, response);
        }));

        this.subscriptions.push(this.agContextMenuService.createTaskClicked.subscribe(async params => {
            // if multiple widgets are opened that support this option we need to assure only the clicked one opens the modal
            if (params.moduleKey !== this.moduleManager.getModuleKey()) {
                return;
            }
            this.openCreateTaskModal(params.moduleKey, params.groupData.groupID, params.groupData);
        }));

        this.subscriptions.push(this.agContextMenuService.giveAwardClicked.subscribe(async params => {
            // if multiple widgets are opened that support this option we need to assure only the clicked one opens the modal
            if (params.moduleKey !== this.moduleManager.getModuleKey()) {
                return;
            }

            this.openAwardModal(params.moduleKey, params.groupData.groupID, params.groupData, this.config.isCxOne)
        }));

        this.subscriptions.push(this.agContextMenuService.suggestCoachingClicked.subscribe(async params => {
            if (params.moduleKey !== this.moduleManager.getModuleKey()) {
                return;
            }
            this.openSuggestCoachingModal(params.moduleKey, params.groupData.groupID, params.groupData);
        }));
    }

    getLocaleText(key: string, defaultValue?: string) {
        return this.cvLocalizationService.getCachedText("Summary_Module", key, defaultValue);
    }

    populateData() {
        // Run when finished loading
        if (this.data && this.moduleState.view === ModuleView.Table || this.moduleState.view === ModuleView.Dual) {
            this.comboLoadDataTable(this.data);
        }

        // Run when finished loading
        if (this.data && this.moduleState.view === ModuleView.Chart || this.moduleState.view === ModuleView.Dual) {
            this.comboLoadDataChart(this.data);
        }
    }

    resetMenuItems() {
        this.moduleManager.resetMenu();

        // assure first load has search icon on table or dual
        if (this.moduleState.view === ModuleView.Table || this.moduleState.view === ModuleView.Dual) {
            this.moduleManager.addHeaderItem({
                icon: "search",
                click: () => this.toggleOpenSearch(),
                name: "searchIcon"
            });
        }

        this.setComboMenu();

        // assures first load has table menu items
        if (this.moduleState.view !== ModuleView.Chart) {
            this.menuItemsForTable();
        }
        if (this.moduleState.view !== ModuleView.Table) {
            this.menuItemsForChart();
        }
        this.setComboMenu();
    }

    menuItemsForTable() {
        this.moduleManager.addMenuItem({
            name: this.getLocaleText('Table Display'),
            icon: "tableDisplay",
            getEnabled: () => this.agGrid?.api && this.gridOptions.columnDefs.length > 0 && !this.noData,
            type: this.isCXOne ? ModuleManagerMenuItemType.CheckMenu : ModuleManagerMenuItemType.RadioCheckMenu,
            getCheckItems: () => {
                let index;
                if (this.data?.rows?.length > 0)
                    index = this.data?.rows[0]?.attributeValue?.length === this.data?.columnDefs?.length ? 2 : 1;
                else
                    index = 1;
                if (this.data?.columnDefs[index + 1]?.name === 'AAgent ID')
                    return this.isCXOne ? this.displayCheckItems : this.tableDisplayItems;

                return this.isCXOne ? this.displayCheckItems.slice(0, 2) : this.tableDisplayItems.slice(0, 4);
            },
            getItemName: i => this.isCXOne ? this.displayCheckItems[i].displayName : this.tableDisplayItems[i].displayName,
            getItemInputType: i => { return i <= this.tableDisplayItems.length - 1 ? this.tableDisplayItems[i]?.inputType : 'checkbox'}, //not checking for cxone here since they wont be using the radio buttons
            isItemChecked: i => this.isCXOne ? this.displayCheckItems[i].checked : this.tableDisplayItems[i].checked,
            onCheckClicked: i => {
                if (!this.isCXOne && this.tableDisplayItems[i].inputType === 'radio') {
                    this.showTotal = this.tableDisplayItems[i].value;
                    this.tableDisplayItems.filter(i => i.inputType === 'radio').forEach((item) => { item.checked = this.showTotal });
                }
                else { 
                    if (this.isCXOne)
                        this.displayCheckItems[i].checked = !this.displayCheckItems[i].checked;
                    else
                        this.tableDisplayItems[i].checked = !this.tableDisplayItems[i].checked;
                }
                this.displayCheckItemClicked(i);
            },
            readOnlyEnabled: this.isCXOne
        } as IModuleManagerMenuItem);
        this.moduleManager.addMenuItem({
            name: this.getLocaleText('Columns'),
            icon: "columns",
            getEnabled: () => this.agGrid?.api && this.agGrid?.api['columnModel'].gridColumns.length > 0 && !this.noData,
            type: ModuleManagerMenuItemType.CheckMenu,
            getCheckItems: () => this.agGrid?.api['columnModel'].gridColumns,
            getItemName: i => this.agGrid?.api['columnModel'].gridColumns[i].colDef.headerName,
            isItemChecked: i => this.agGrid?.api['columnModel'].gridColumns[i].isVisible(),
            onCheckClicked: i => this.toggleColumnVisible(i),
        } as IModuleManagerMenuItem);
    }

    menuItemsForChart() {
        if (!this.config.isCxOne) {
            this.moduleManager.addMenuItem({
                name: this.getLocaleText('Themes'),
                icon: 'palette',
                type: ModuleManagerMenuItemType.SubMenu,
                subItems: this.themeMenuOptions,
                getEnabled: () => this.data != null,
            } as ModuleManagerSubMenu);
        }

        this.moduleManager.addMenuItem({
            name: this.getLocaleText('Chart_Type'),
            icon: 'chartType',
            type: ModuleManagerMenuItemType.SubMenu,
            getEnabled: () => this.chartObject != null,
            subItems: [{
                name: this.getLocaleText('Line'),
                icon: 'lineChart',
                click: () => this.setChartType('line')
            },
            {
                name: this.getLocaleText('Vertical_Bar'),
                icon: 'barChart',
                click: () => this.setChartType('bar')
            },
            {
                name: this.getLocaleText('Horizontal_Bar'),
                icon: 'horizontalBarChart',
                click: () => this.setChartType('horizontalBar')
            }],
            readOnlyEnabled: this.isCXOne
        } as ModuleManagerSubMenu);

        this.moduleManager.addMenuItem({
            name: this.getLocaleText('Summary_Module', 'Legend_Position'),
            icon: 'legend',
            type: ModuleManagerMenuItemType.SubMenu,
            getEnabled: () => this.chartObject != null,
            subItems: [{
                name: this.getLocaleText('Top'),
                icon: 'positionTop',
                click: () => this.setLegendPosition('top', true),
            },
            {
                name: this.getLocaleText('Bottom'),
                icon: 'positionBottom',
                click: () => this.setLegendPosition('bottom', true),
            },
            {
                name: this.getLocaleText('Left'),
                icon: 'positionLeft',
                click: () => this.setLegendPosition('left', true),
            },
            {
                name: this.getLocaleText('Right'),
                icon: 'positionRight',
                click: () => this.setLegendPosition('right', true),
            },
            {
                type: 'divider'
            },
            {
                name:  !this.moduleState.graphState.showLegendPosition ? this.getLocaleText('Show_Legend_Chart','Show legend') : this.getLocaleText('Hide_Legend_Chart','Hide legend'),
                icon: "eye",
                click: () => {
                    this.hideShowLegendClicked = true;
                    this.setLegendPosition('hide', true);
                }
            }],
            readOnlyEnabled: this.isCXOne
        } as ModuleManagerSubMenu);
    }

    // this gets called when the view changes from chart table or dual
    toggleHeaderFilterButton(activeView: string) {
        if (activeView === ModuleView.Chart) {
            delete this.moduleManager.additionalHeaderItems["searchIcon"];
        } else {
            this.moduleManager.addHeaderItem({
                icon: "search",
                click: () => this.toggleOpenSearch(),
                name: "searchIcon"
            });
        }
    }

    /**
     * used to turn the legend off and on via the legend context menu item.
     */
    toggleChartLegend() {
        if (this.chartObject) {
            requestAnimationFrame(() => {
                this.chartObject.chart.options.plugins.legend.display = this.moduleState.graphState.showLegendPosition;
                this.chartObject.chart.update();
            });
        }
    }

    async loadData(isAutoRefresh) {
        if (this.dataLoadPromise) { // cancel current load
            this.isAborting = true;
            this.abortLoad?.();
            this.dataLoadPromise = false;
            this.abortLoad = null;
        }

        if (!isAutoRefresh) {
            this.moduleManager.showLoader = true;
        }

        this.dataLoading = true;
        const abort = new Promise<void>((resolve, reject) => this.abortLoad = () => resolve());
        this.dataLoadPromise = this.service.LoadPerformanceData(this.moduleKey, isAutoRefresh, this.isFirstLoad, abort);

        try {
            this.data = await this.dataLoadPromise; // returns response.data
            try {
                this.metricThresholds = await this.thresholdService.loadWidgetThresholds(this.moduleKey);
            } catch (e) {
                this.metricThresholds = {};
            }
            if (!this.data || this.data.rows?.length === 0) {
                this.noData = true;
                this.resetMenuItems();
            } else if (this.data.Error === ErrorCode.NullMetricData) {
                this.noData = true;
                this.dataLoading = false;
                this.moduleManager.showLoader = false;
                this.dataLoadPromise = false;
                this.dataLoadSuccess = true;
                this.isManualRefresh = false;
                this.resetMenuItems();
                return;
            } else if (this.data.Error === ErrorCode.NoMetric) {
                this.isNoMetric = true;
                this.dataLoading = false;
                this.moduleManager.showLoader = false;
                this.dataLoadPromise = false;
                this.dataLoadSuccess = true;
                this.isManualRefresh = false;
                this.resetMenuItems();
                return;
            } else if (this.data.Error === ErrorCode.DateRangeTooWideIntraday) {
                this.isDateRangeTooWideIntraday = true;
                this.dataLoading = false;
                this.moduleManager.showLoader = false;
                this.dataLoadPromise = false;
                this.dataLoadSuccess = true;
                this.isManualRefresh = false;
                this.resetMenuItems();
                return;
            } else {
                this.noData = false;
            }
        } catch (e) {
            if (this.isAborting || e?.code === DOMException.ABORT_ERR || e?.statusText === 'abort') {
                // failed because we cancelled the request
                this.isAborting = false;
                return;
            }
            this.moduleManager.handleLoadError(e, isAutoRefresh);
        }

        this.isNoMetric = false;
        this.isDateRangeTooWideIntraday = false;

        this.resetMenuItems();

        this.dataLoadSuccess = true;
        this.moduleManager.showLoader = false;
        this.moduleManager.setAutoRefreshReady();

        // Reset loader flags
        this.dataLoading = false;
        this.dataLoadPromise = null;
        this.isManualRefresh = false;
        this.isFirstLoad = false;
        this.moduleManager.container.showDateRange = true;
    };

    // Menu Items
    // Set menu items that are always available available through the combination parent.
    setComboMenu() {
        const displayItems = [];
        const options = this.moduleManager.getModuleInfo().options;

        if (this.isCXOne && this.moduleState.view !== ModuleView.Chart) {
            this.moduleManager.addMenuItem({
                name: this.getLocaleText('Thresholds'),
                icon: "thresholds",
                getEnabled: () => !this.isNoMetric && !this.noData,
                type: ModuleManagerMenuItemType.MenuItem,
                readOnlyEnabled: this.isCXOne,
                click: () => this.openThresholdsModal(this.moduleKey),
                tooltip: this.moduleManager.container.noMetricsSelected() ?
                    this.getLocaleText("No_Metric_Message", "No metrics selected yet") : null,
            } as IModuleManagerMenuItem);
        }

        if (!options?.tableAvailable || !options?.chartAvailable)
            return; // only one view so we don't need this menu

        if (options.tableAvailable) {
            displayItems.push({
                name: this.getLocaleText('Table'),
                icon: 'tableView',
                getEnabled: () => true,
                click: () => {
                    this.setModuleState(ModuleView.Table);
                    this.toggleHeaderFilterButton(ModuleView.Table);
                }
            });
        }
        if (options.chartAvailable) {
            displayItems.push({
                name: this.getLocaleText('Chart'),
                icon: 'chartView',
                getEnabled: () => true,
                click: () => {
                    this.setModuleState(ModuleView.Chart);
                    this.toggleHeaderFilterButton(ModuleView.Chart);
                }
            });
        }
        if (options.tableAndChartAvailable) {
            displayItems.push({
                name: this.getLocaleText('Table_Chart'),
                icon: 'tableAndChartView',
                getEnabled: () => true,
                click: () => {
                    this.setModuleState(ModuleView.Dual);
                    this.toggleHeaderFilterButton(ModuleView.Dual);
                }
            });
        }
        this.moduleManager.addMenuItem({
            name: this.getLocaleText('Widget_Display'),
            icon: 'widgetType',
            type: ModuleManagerMenuItemType.SubMenu,
            getEnabled: () => this.dataLoadSuccess && !this.isNoMetric && !this.noData,
            subItems: displayItems,
            readOnlyEnabled: this.isCXOne
        } as ModuleManagerSubMenu);
    }

    // States
    setModuleState(state) {
        if (this.moduleState.view === state) {
            return;
        }
        this.zoom.cur = 1;
        this.moduleManager.resetMenu();
        this.setComboMenu();
        this.moduleState.view = state;

        switch (this.moduleState.view) {
            case (ModuleView.Table):
                this.singleMode = true;
                this.dualMode = false;
                this.moduleState.view = ModuleView.Table;
                this.menuItemsForTable();
                break;
            case (ModuleView.Chart):
                this.singleMode = true;
                this.dualMode = false;
                this.moduleState.view = ModuleView.Chart;
                this.menuItemsForChart();
                this.createChart();
                break;
            case (ModuleView.Dual):
                this.singleMode = false;
                this.dualMode = true;
                this.moduleState.view = ModuleView.Dual;
                this.menuItemsForTable();
                this.createChart();
                break;
            default:
                this.singleMode = true;
                this.dualMode = false;
                break;
        }

        this.resetMenuItems();

        this.moduleManager.saveState();
        this.populateData();
        if (this.moduleState.view === ModuleView.Table) {
            setTimeout(() => {
                this.agGridHelperService.sizeColumnsFromApisSmart(this.agGrid?.api);
                this.tableStateChanged();
            });
        }
        this.moduleManager.requestPublishNeeded();
    }

    /**
     * Called when table state is loaded in. Often, this will happen after the grid is ready, but for this widget, the column defs come from
     * the data. So it may be triggered from onStateLoaded of tableState, or onGridReady. Whichever function is last makes this call.
     *
     * @param stateData State data that is brought in. If this is being called from onStateLoaded, it will pass in the value found there. Otherwise
     *  it will use this.tableState (which is a copy of the value from onStateLoaded)
     * @returns void
     */
    onTableStateLoaded(stateData) {
        if (stateData) {
            this.tableState.columns.forEach((el, i) => {
                // Location or Parent
                if (el.name === "parentGroupData") {
                    if (el.visible) {
                        this.parentVisible = true;
                        for (const checkItem of (this.isCXOne ? this.displayCheckItems : this.tableDisplayItems)) {
                            if (checkItem.displayName === this.getLocaleText('Show_Parent_Level')) {
                                checkItem.checked = this.parentVisible;
                                break;
                            }
                        }
                    }
                } else if (el.name === 'AAgent ID') { // Agent Info Columns
                    if (el.visible) {
                        this.toggleAgentIdColumn();
                    }
                } else if (el.name === 'AAgent Email') {
                    if (el.visible) {
                        this.toggleAgentEmailColumn();
                    }
                }
            });
        }
    }

    /**
     * Loads the data for the table
     */
    comboLoadDataTable(data) {
        if (this.noData) {
            return;
        }

        this.data = data;
        if (this.data.Error === 1 || this.data.Error === 2) {
            return;
        }
        const rowData = [];
        let index = 0;
        if (this.data.rows != null && this.data.rows.length >= 1) {
            index = this.data.rows[0]?.attributeValue?.length === this.data?.columnDefs?.length ? 1 : 0;
        }
        for (let i = 0; i < this.data.rows.length; i++) {
            const newRow = {
                ID: i,
                Group: this.data.rows[i].group,
                'AAgent ID': this.data.rows[i].attributeValue[index + 1],
                'AAgent Email': this.data.rows[i].attributeValue[index + 2],
                groupData: this.data.rows[i].group,
                moduleKey: this.moduleKey,
                groupLevel: this.moduleManager.moduleSettings.selectedGroupLevel,
                parentGroupData: this.data.rows[i].locationGroup, // FIX would need to change cSharpSide here for name parentGroup renamed from location to parent
                parentGroupLevel: this.data.rows[i].locationGroup.groupLevel,
            };
            let j;
            for (j = 1; j < this.data.rows[i].metricData.length; j++) {
                try {
                    const currentMetric = this.data.rows[i].metricData[j];
                    currentMetric.MetricType = this.data.rows[i].metricTypes[j];
                    currentMetric.MetricDisplayValue = this.data.rows[i].metricData[j].MetricDisplayValue;
                    if (currentMetric.MetricType === 2) {
                        currentMetric.MetricValue = parseFloat((currentMetric.MetricValue * 100).toFixed(2));
                    }
                    currentMetric.Value = currentMetric.MetricType === NumericType.Time ?
                        parseFloat(currentMetric.MetricDisplayValue) : this.data.rows[i].metricData[j].MetricValue;
                    newRow["Metric" + currentMetric.MetricID] = currentMetric;
                } catch (ex) { // The metric does not exist or does not have a MetricID
                    // This is generally okay; blank cells aren't given an associated MetricID in the endpoint
                }
            }
            for (j = 1; j < this.data.rows[i].attributeValue.length; j++) {
                const currentAttrID = this.data.rows[i].attributeIDs[j];
                const currentAttr = this.data.rows[i].attributeValue[j];
                if (currentAttrID > 0 && currentAttr != null) newRow["UAttribute" + currentAttrID] = currentAttr;
            }
            rowData.push(newRow);
        }
        this.gridOptions.rowData = rowData;
        if (this.agGrid?.api) { // if the grid is already loaded, we have to use the api to notify agGrid
            this.agGrid?.api.setGridOption('rowData', rowData);
            this.agGrid?.api.redrawRows();
        }

        this.updateColumnDefs();// update column defs if needed
    };

    /////////////////////////////////////////////////////////////////////////////////////////////
    /////////////////////////////////////////////////////////////////////////////////////////////

    ///////////////////////////////////// SUMMARY CHART /////////////////////////////////////////

    /////////////////////////////////////////////////////////////////////////////////////////////
    /////////////////////////////////////////////////////////////////////////////////////////////

    // eslint-disable-next-line @typescript-eslint/member-ordering
    @HostListener('wheel', ['$event']) onMouseWheel(event: WheelEvent) {
        if (this.shouldHandleZoom(event)) {
            this.handleZoom(event.deltaY);
            event.preventDefault(); // Prevent the default behavior only when handling zoom
        }
    };

    // ensures the onMouseWheel override only occurs when the cursor is over the chart area
    shouldHandleZoom(event) {
        const targetElement = document.elementFromPoint(event.clientX, event.clientY);
        const chart = this.chartObject?.chart.canvas.parentElement.parentElement;
        return chart?.contains(targetElement);
    }

    comboLoadDataChart(data) {
        if (this.noData) {
            return;
        }
        this.setLegendPosition(this.moduleState.graphState.legendPosition, false);
        // this.setThemeClick(false);
        this.createChart();
    };

    ngOnDestroy(): void {
        this.modalRef?.dismiss();
        this.subscriptions.forEach(sub => {
            sub.unsubscribe();
        });
    }

    // Manage graph/chart state
    async setChartType(type) {
        // Set type string, recreate the chart.
        // Type change is a big update for Chart.js, so the chart must be recreated.
        this.zoom.cur = 1;
        const typeChanged = !(this.moduleState.graphState.chartType === type);
        this.moduleState.graphState.chartType = type;
        this.createChart();
        if (typeChanged) {
            await this.moduleManager.saveState('performanceComboState');
            this.moduleManager.requestPublishNeeded();
        }
    }

    async setLegendPosition(position, recreateChart) {
        // Set position string
        if (recreateChart) {
            this.moduleState.graphState.showLegendPosition = !this.moduleState.graphState.showLegendPosition;
            if (position !== 'hide') {
                this.moduleState.graphState.showLegendPosition = true;
            }
            if (position === 'hide') {
                position = this.moduleState.graphState.legendPosition;
            }
        }

        this.chartLegendPosition = position;
        this.moduleState.graphState.legendPosition = position;
        // Data load? Fix
        // Set padding values
        switch (position) {
            case ('top'): // Default view, Chart.js defaults all padding values to 0.
                this.chartPadding = {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                };
                break;
            case ('bottom'): // Extra top space. Bottom is padded by the legend, so no extra bottom padding.
                this.chartPadding = {
                    top: 16,
                    bottom: 0,
                    left: 0,
                    right: 0,
                };
                break;
            case ('left'): // Extra top and bottom space
                this.chartPadding = {
                    top: 16,
                    bottom: 8,
                    left: 0,
                    right: 0,
                };
                break;
            case ('right'): // Extra top and bottom space
                this.chartPadding = {
                    top: 16,
                    bottom: 8,
                    left: 0,
                    right: 0,
                };
                break;
            default: // Same as 'top'
                this.chartPadding = {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                };
                break;
        }

        // Apply changes
        // Moving the legend requires the chart to be completely recreated instead of just updated.
        // This method (using a variable rather than direct manipulation) also allows the padding values to be retained if the chart is recreated in any way.
        if (recreateChart) {
            this.createChart();
            await this.moduleManager.saveState('performanceComboState');
            this.moduleManager.requestPublishNeeded();
        }
    }

    legendClickHandler(e, item) {
        const index = item.datasetIndex;
        const meta = this.chartObject.chart.getDatasetMeta(index);

        meta.hidden = !this.legendItemsHidden.get(item.text);

        this.legendItemsHidden.set(item.text, meta.hidden);
        if (this.moduleState.graphState.chartType === 'horizontalBar') {
            if (meta.hidden) {
                this.chartAxisShowStates[meta.xAxisID] -= 1;
                if (this.chartAxisShowStates[meta.xAxisID] === 0) {
                    this.chartObject.chart.options.scales[meta.xAxisID].display = false;
                }
            } else {
                this.chartAxisShowStates[meta.xAxisID] += 1;
                this.chartObject.chart.options.scales[meta.xAxisID].display = true;
            }
        }
        else {
            if (meta.hidden) {
                this.chartAxisShowStates[meta.yAxisID] -= 1;
                if (this.chartAxisShowStates[meta.yAxisID] === 0) {
                    this.chartObject.chart.options.scales[meta.yAxisID].display = false;
                }
            } else {
                this.chartAxisShowStates[meta.yAxisID] += 1;
                this.chartObject.chart.options.scales[meta.yAxisID].display = true;
            }
        }

        this.chartObject.chart.update();
    }

    createChart() {
        // Check for null/empty data sets
        // If found, create an empty chart.
        if (!this.data || this.data.rows.length === 0) {
            this.chartConfig = {
                //chartConfig still uses 'bar' type for horizontal bar charts
                type: (this.moduleState.graphState.chartType === 'horizontalBar') ? 'bar' : this.moduleState.graphState.chartType,
                data: {
                    labels: ['0'],
                    datasets: [{
                        label: '',
                        backgroundColor: 'rgba(0,0,0,0)',
                        borderColor: 'rgba(0,0,0,0)',
                        data: []
                    }]
                },
                options: {
                    plugins: {
                        emptyChart: {
                            disableCxone: this.config.isCxOne,
                        },
                    },
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: {
                        duration: 0,
                    },
                    scaleShowGridLines: true,
                    scaleShowVerticalLines: true,
                    showTooltips: false,
                    scales: {
                        y: {
                            beginAtZero: true
                        }
                    },
                    //this is used to make the bar chart horizontal
                    indexAxis: this.moduleState.graphState.chartType === 'horizontalBar' ? 'y' : 'x',
                },
                plugins: [this.backgroundcolor]
            } as ChartConfiguration;
            return;
        }

        if(this.metricSort){
            //here i'm getting the index for the metricData that is going to be used on the sortMetricData functions
            let metricDataIndex = 0;
            if (this.data.rows.length > 0) {
                for (const row of this.data.rows) {
                    const index = row.metricData.findIndex((metricData) => metricData?.MetricID === this.sortID);
                    if (index !== -1) {
                        metricDataIndex = index;
                        break;
                    }
                }
            }

            if(this.metricSort === 'asc'){
                this.data.rows = this.sortMetricDataAscending(this.data.rows, metricDataIndex);
            } else {
                this.data.rows = this.sortMetricDataDescending(this.data.rows, metricDataIndex);
            }
        }
        else if(this.groupSort){
            if(this.groupSort === 'asc'){
                this.data.rows = this.sortGroupDataAscending(this.data.rows);
            } else {
                this.data.rows = this.sortGroupDataDescending(this.data.rows);
            }
        }
        else{
            //this is the default sort
            this.data.rows = this.sortGroupDataAscending(this.data.rows);
        }

        const preppedDatasets = [];
        const seriesTypes = [];
        const xAxisLabels = [];
        const labels = [];
        const metricIDs = [];
        const datasetMetricIDs = [];
        const preparedYAxes = {};
        this.chartAxisShowStates = {};

        // Map Labels and Metric IDs to their containers.
        // This is done outside of group instantiation to only iterate over the collection once.
        this.tooltipLegendItems.clear();
        let uncountedMetrics = 0;
        const hasGroup = this.data.columnDefs.some(c => c.displayName === "Group") ? 1 : 0;
        this.data.columnDefs.map((c, index) => {
            if ((typeof c.field === "undefined" && c.metricID > 0) || c.displayName === "Group") {
                const isRenamed = c.customName && c.customName !== "";
                labels.push(isRenamed ? c.customName + "*" : c.displayName);
                metricIDs.push(c.metricID);
                if (isRenamed)
                    this.tooltipLegendItems.set(
                        index - hasGroup - uncountedMetrics, this.cvLocalizationService.getCachedText(
                            "Common", "Original_Metric", "Original Metric Name") + ": " + "\n" + c.displayName);
            }
            else
                uncountedMetrics++;
        });

        if (!this.firstAnimateComplete) {
            labels.forEach(metric => {
                this.legendItemsHidden.set(metric, false);
            });
        }

        // Then shift() both collections to remove the first element.
        // [0] is the "Groups" column, which isn't needed in the chart. Groups will be handled when parsing rows.
        metricIDs.shift();

        const transparentColor = 'rgba(0,0,0,0)'; // For the Alpha=0 value. Draws the border (which allows the under-the-line fill) but sets the actual line to be transparent.
        //swap to y when horizontal bar chart is selected
        const xAxisId = this.moduleState.graphState.chartType === 'horizontalBar' ? 'y' : 'x'; // All items go on the same X-Axis, so we need an identifier for this one X-Axis.
        let colorIndex = 0;

        // Ensure a theme exists
        if (this.currentTheme == null) {
            this.currentTheme = this.moduleThemesService.ModuleThemes[0].Colors;
        }
        if (this.config.isCxOne) {
            this.currentTheme = this.moduleThemesService.ModuleThemes[12].Colors;
        }

        // Create datasets, iterate over each row
        for (let i = 0; i < this.data.rows.length; i++) {
            xAxisLabels.push(this.data.rows[i].group.groupName);

            // Iterate over the row's metrics collection for their values. Skip [0] (start @ m = 1), as it's always null.
            for (let m = 1; m < this.data.rows[i].metricData.length; m++) {
                if (this.data.rows[i].metricTypeNames[m] === "Attribute") {
                    continue;
                }

                // Create Y-Axes
                const yAxisId = (this.data.rows[i].metricTypes[m] != null)
                    ? ''.concat('YAxisType-', this.data.rows[i].metricTypes[m].toString())
                    : 'YAxisType-1'; // Some null metric types are coming through from the endpoint. Make the assumption that the interval endpoint does and stick null types as a general-type "Number."

                // Metrics should be the same for each column in the performance data. So we only need to manage axes on the first entry.
                // This will iterate over each column (index-m) in the first row (index-i), extracting the data types. After row[0], we don't need to re-run this logic. Thus, if(i==0).
                if (i === 0) {
                    const nullCheckedMetricType = (this.data.rows[i].metricTypes[m] != null) ? this.data.rows[i].metricTypes[m] : 1;

                    // Check if a Y-Axis for this NumericType already exists, creating a new one if not.
                    if (seriesTypes.indexOf(nullCheckedMetricType) < 0) {
                        this.chartAxisShowStates[yAxisId] = 1;

                        const nullCheckedMetricTypeName = this.data.rows[i].metricTypeNames[m] !== null ?
                            this.data.rows[i].metricTypeNames[m] : 'Number';
                        seriesTypes.push(nullCheckedMetricType);

                        // Check for Type 3 (Time) metrics. The scale requires special axis-tick formatting. If detected, push an axis with the right configuration. Othewise, use the default.
                        if (this.data.rows[i].metricTypes[m] === NumericType.Time &&
                            this.data.rows[i].metricTimeTypes[m] === TimeType.Time) {
                            Object.assign(preparedYAxes, {
                                [yAxisId]:
                            {
                                id: yAxisId,
                                title: {
                                    display: true,
                                    text: nullCheckedMetricTypeName,
                                    font: {
                                        weight: 'bold',
                                    },
                                },
                                display: true, // reset in verifyYAxisDisplay()
                                position: this.determinePosition(seriesTypes.length),
                                ticks: {
                                    callback: function (value) {
                                        const hours = Math.floor(value / 3600);
                                        const minutes = Math.floor((value % 3600) / 60);
                                        const seconds = Math.floor(value % 60);
                                        return hours.toString().padStart(2, '0') + ":" + minutes.toString().padStart(2, '0') + ":" + seconds.toString().padStart(2, '0');
                                    }
                                }
                            }
                            });
                        } else {
                            // Default axis config (Number, Percentage, Currency)
                            Object.assign(preparedYAxes, {
                                [yAxisId]:
                            {
                                id: yAxisId,
                                title: {
                                    display: true, // reset in verifyYAxisDisplay()
                                    text: nullCheckedMetricTypeName,
                                    font: {
                                        weight: 'bold',
                                    },
                                },
                                display: true,
                                position: this.determinePosition(seriesTypes.length),
                            }
                            });
                        }

                    } else {
                        this.chartAxisShowStates[yAxisId] += 1;
                    }
                }

                const metricVal = this.data.rows[i].metricData[m]?.MetricValue ?? 0;
                const metricDisplayVal = this.data.rows[i].metricData[m]?.MetricDisplayValue ?? "0";

                // Get the matched data type.
                const matchedDatasetIndex = datasetMetricIDs.indexOf(metricIDs[m]);

                if (matchedDatasetIndex < 0) {
                    // Add the series to be displayed
                    // For grouped bar charts, push multiple points into the same label.
                    // Skip the first, the performance endpoint puts [0]=null to help render the Performance Table.
                    datasetMetricIDs.push(metricIDs[m]);

                    preppedDatasets.push({
                        //x and y axisIDs neet to be swapped for horizontal bar charts
                        xAxisID: this.moduleState.graphState.chartType === 'horizontalBar' ? yAxisId : xAxisId,
                        yAxisID: this.moduleState.graphState.chartType === 'horizontalBar' ? xAxisId : yAxisId,
                        label: labels[m],
                        backgroundColor: this.currentTheme[colorIndex],
                        borderColor: this.currentTheme[colorIndex],
                        data: [metricVal],
                        displayValues: [metricDisplayVal],
                        dataType: (this.data.rows[i].metricTypeNames[m] != null) ? this.data.rows[i].metricTypeNames[m] : 'Number',
                        fill: this.data.rows.length > 5 ? false : 'origin',
                        hitRadius: 12,
                        hoverRadius: 6,
                        pointRadius: 2,
                        pointBorderColor: this.currentTheme[colorIndex],
                        pointBackgroundColor: this.currentTheme[colorIndex],
                        hidden: this.legendItemsHidden.get(labels[m]),
                    });
                    if (this.legendItemsHidden.get(labels[m])) { // if it has been previously hidden update the count of metrics tied to specific y-axis
                        this.chartAxisShowStates[yAxisId] -= 1;
                    }
                    // Adjust color picker stuff for next series
                    if (++colorIndex >= this.currentTheme.length) {
                        colorIndex = 0;
                    }
                } else {
                    preppedDatasets[matchedDatasetIndex].data.push(metricVal);
                    preppedDatasets[matchedDatasetIndex].displayValues.push(metricDisplayVal);
                }
            }
        }

        if (!this.moduleState.graphState) {
            this.moduleState.graphState = {
                chartType: 'bar',
                legendPosition: 'top',
                axisShowStates: {},
                themeIndex: 0,
                showLegendPosition: true
            };
        }

        this.verifyYAxisDisplay(preparedYAxes);
        let hovering = false;
        const tooltip = document.getElementsByClassName("legendTooltipSummary")[0];
        const tooltips = this.tooltipLegendItems;
        this.chartConfig = {
            //Horizontal bar chart is still a bar chart in chart.js so we need to set the type to bar
            type: this.moduleState.graphState.chartType === 'horizontalBar' ? 'bar' : this.moduleState.graphState.chartType,
            data: {
                datasets: preppedDatasets,
                labels: xAxisLabels
            },
            options: { // Please keep option keys in alphabetical order for future searchability

                /* --- Single Value Options --- */
                maintainAspectRatio: false, // Disable to keep responsiveness, but stop pushing data off the bottom of the module pane
                responsive: true,

                /* --- Object Based Options --- */
                animation: {
                    duration: this.config.isCxOne ? 0: 1,
                },
                elements: {
                    line: {
                        tension: 0.3
                    }
                },
                hover: {
                    intersect: true,
                    mode: this.config.isCxOne ? 'point' : 'index'
                },
                //This is used to set the bar chart to horizontal
                indexAxis: this.moduleState.graphState.chartType === 'horizontalBar' ? 'y' : 'x',
                layout: {
                    padding: this.chartPadding
                },
                interaction: {
                    mode: this.config.isCxOne ? "point" : 'index',
                    intersect: false,
                },
                plugins: {
                    decimation: {
                        enabled: true,
                        algorithm: 'lttb',
                        samples: 500,
                    },
                    emptyChart: {
                        disableCxone: this.config.isCxOne,
                    },
                    legend: {
                        onClick: this.legendClickHandler.bind(this),
                        display: this.moduleState.graphState.showLegendPosition,
                        position: this.chartLegendPosition,
                        onHover: (event, legendItem) => {
                            if (hovering || !tooltips.has(legendItem.datasetIndex)) {
                                return;
                            }
                            hovering = true;
                            tooltip.innerHTML = tooltips.get(legendItem.datasetIndex);
                            tooltip.setAttribute('style', `left:${event.x}px; right:${event.y}px; visibility:visible`);
                        },
                        onLeave: () => {
                            hovering = false;
                            tooltip.innerHTML = "";
                            tooltip.setAttribute('style', `visibility:hidden`);
                        },
                        labels: {
                            boxWidth: 16,
                        }
                    },
                    title: {
                        display: false,
                        text: '',
                    },
                    tooltip: {
                        animation: false,
                        padding: this.config.isCxOne ? 12 : 6,
                        usePointStyle: true,
                        boxPadding: this.config.isCxOne ? 3 : 1,
                        boxWidth: this.config.isCxOne ? 11 : 18,
                        boxHeight: this.config.isCxOne ? 11 : 18,
                        borderColor: this.config.isCxOne ? '#D0D2D3' : '#353a40',
                        titleFont: {
                            size: this.config.isCxOne ? 12 : 16,
                            weight: this.config.isCxOne ? "normal" : "bold",
                        },
                        titleColor: this.config.isCxOne ? '#8c95a5' : '#fff',
                        bodyColor: this.config.isCxOne ? '#353a40' : '#fff',
                        footerColor: '#353a40',
                        backgroundColor: this.config.isCxOne ? '#FFF' : 'rgba(0, 0, 0, 0.8)',
                        bodyFont: {
                            size: 14,
                        },
                        footerFont: {
                            size: 14,
                            weight: 'bold',
                        },
                        borderWidth: this.config.isCxOne ? 1 : 0,
                        cornerRadius: this.config.isCxOne ? 0 : 6,
                        callbacks: {
                            // tooltipItem is an array of all Y-values for the given X-value/position being hovered over.
                            // tooltipItem should always have something at index [0] regardless of how many are in the series.
                            // So, get the series-data index of tooltipItem[0], which is used to pull the right x-value label.
                            title: (tooltipItem) => this.config.isCxOne ? this.datePipe.transform(new Date(), "MMM dd, yyyy") :
                                tooltipItem[0].label,
                            label: (tooltipItem) => {
                                const bodyPm = tooltipItem.dataset.label + ": " +
                                    (tooltipItem.dataset as any).displayValues[tooltipItem.dataIndex];
                                return this.config.isCxOne ? tooltipItem.dataset.label : bodyPm;
                            },
                            footer: tooltipItem => this.config.isCxOne ?
                                "     " + (tooltipItem[0].dataset as any).displayValues[tooltipItem[0].dataIndex] : "",
                            labelColor: tooltipItem => ({
                                borderColor: tooltipItem.dataset.borderColor,
                                backgroundColor: tooltipItem.dataset.backgroundColor,
                                borderWidth: this.config.isCxOne ? 0 : 0.5,
                            }),
                            labelPointStyle: context => ({
                                pointStyle: this.config.isCxOne ? 'circle' : 'rect',
                                rotation: 0,
                            }),
                        },
                        intersect: true, //this gets the tooltip in the correct position for horizontal bar charts
                    },
                },
                point: {
                    hitRadius: this.config.isCxOne ? 1 : 4,
                    hoverRadius: 4,
                    pointRadius: 0
                },
                scales: {
                    //internally these are XAxes when working with horizontal bar charts
                    ...preparedYAxes,
                },
            },
            plugins: [this.backgroundcolor]
        } as ChartConfiguration;

        const scale = {
            id: xAxisId,
            display: true,
            distribution: 'series',
            scaleLabel: {
                display: false,
            },
            ticks: {
                autoSkip: true,
                source: 'labels',
            }
        };
        if (this.moduleState.graphState.chartType === 'horizontalBar')
            this.chartConfig.options.scales.y = scale;
        else
            this.chartConfig.options.scales.x = scale;

        if (this.chartConfig.data.datasets.length === 0) {
            if (this.moduleState.graphState.chartType === 'horizontalBar')
                this.chartConfig.options.scales.y.display = false;
            else
                this.chartConfig.options.scales.x.display = false;
        } else {
            if (this.moduleState.graphState.chartType === 'horizontalBar')
                this.chartConfig.options.scales.y.display = true;
            else {
                this.chartConfig.options.scales.x.display = true;
            }
        }

        // updates legend visibility
        if (this.hideShowLegendClicked) {
            this.toggleChartLegend();
            this.hideShowLegendClicked = false;
        }
        // Create the chart
        // assures menu items are created whenever a chart exists
        this.menuItemsForChart();
        this.handleZoom();
        this.firstAnimateComplete = true;
    };

    handleZoom(deltaY: number = 0) {
        if (this.noData || !this.chartObject)
            return;

        const temp = this.zoom.cur + 0.1 * ((deltaY > 0) ? -1 : (deltaY < 0) ? 1 : 0);
        if (temp > this.zoom.max)
            this.zoom.cur = this.zoom.max;
        else if (temp < this.zoom.min)
            this.zoom.cur = this.zoom.min;
        else
            this.zoom.cur = temp;

        if (this.moduleState.graphState.chartType === 'horizontalBar') {
            this.chartObject.chart.canvas.parentElement.style.height = `max(100%,${100 * this.zoom.cur}%)`;
            this.chartObject.chart.canvas.parentElement.style.width = '100%';
            this.chartConfig.options.scales.y.ticks.maxTicksLimit = Math.round(this.zoom.cur * this.zoom.baseTicks);
        }
        else { // all other charts, vertical bar chart or line chart
            this.chartObject.chart.canvas.parentElement.style.width = `max(100%,${100 * this.zoom.cur}%)`;
            this.chartObject.chart.canvas.parentElement.style.height = '100%';
            this.chartConfig.options.scales.x.ticks.maxTicksLimit = Math.round(this.zoom.cur * this.zoom.baseTicks);
        }
        this.chartObject.chart.update();
    }

    verifyYAxisDisplay(preparedYAxes) {
        for (const key in preparedYAxes) {
            if (this.chartAxisShowStates[key] === 0) {
                preparedYAxes[key].display = false;
            }
        }
    }

    exportPNG() {
        // toBase64Image() returns a png data url of the image on the canvas. (Sourced: https://www.chartjs.org/docs/latest/developers/api.html)
        // name of the file
        const date = (new Date()).toString('yyyy_MM_dd_HH_mm_ss');
        const fileName = this.cvLocalizationService.getCachedText("Module_Names", "CXone_SkillSummary", "CXone_SkillSummary")
            .replace(/\s+/g, '-').toLowerCase() + '_widget_(' + date + ').png';

        // create fake link
        const link = document.createElement('a');
        link.setAttribute('download', fileName);
        link.setAttribute('href', this.chartObject.chart.toBase64Image());

        // start download
        link.click();

        // cleanup
        URL.revokeObjectURL(link.href);
        link.remove();
    }

    // must be called after getModuleThemesMenu which populates this.themeMenuOptions
    setThemeClick() {
        for (const theme of this.themeMenuOptions) {
            theme.click = () => {
                this.moduleState.graphState.themeIndex = theme.themeIndex;
                this.currentTheme = this.moduleThemesService.GetThemeByIndex(this.moduleState.graphState.themeIndex);

                this.createChart();

                this.queueStateSave();
            };
        }
    }

    determinePosition(numSeries: number): string {
        // 4 total types (enums: 0,1,2,3). The first two encountered are put on the left, the rest are put on the right for vertical bar; bottom then top for horizontal bar.
        if (this.moduleState.graphState.chartType === 'horizontalBar')
            return numSeries <= 2 ? 'bottom' : 'top';
        else
            return numSeries <= 2 ? 'left' : 'right';
    }

    /////////////////////////////////////////////////////////////////////////////////////////////
    /////////////////////////////////////////////////////////////////////////////////////////////

    ///////////////////////////////////// SUMMARY TABLE /////////////////////////////////////////

    /////////////////////////////////////////////////////////////////////////////////////////////
    /////////////////////////////////////////////////////////////////////////////////////////////

    displayCheckItemClicked(i) {
        if (this.isCXOne) {
            switch (i) {
                case 0: // Show Totals
                    this.toggleFooterVisible();
                    break;
                case 1:
                    this.toggleParentVisible();
                    break;
                case 2:
                    this.toggleBothAgentColumns();
                    break;
            }
        }
        else {
            switch (i) {
                case 0: // Show Totals                
                case 1:
                    this.setFooterVisible(this.showTotal, true);
                    break;
                case 2: break; // ignore for divider 
                case 3:
                    this.toggleParentVisible();
                    break;
                case 4:
                    this.toggleBothAgentColumns();
                    break;
            }
        }
    };

    metricValueGetter(e) {
        const data = e.data[e.colDef.field];

        if (data) {
            return data.value;
        } else {
            return null;
        }
    }

    // applies all the options saved in tableState to the grid
    restoreColumnState() {
        if (!this.tableState?.columns || !this.agGrid?.api || this.agGrid?.api.getColumnDefs().length < 1) {
            return;
        }

        this.agGridHelperService.loadGridState(this.agGrid.api, this.tableState);
    };

    setShowParentStatus() {
        const parentGroupColumn = this.tableState?.columns?.find((c) => c.name == "parentGroupData")
        if (!parentGroupColumn) return;
        this.displayCheckItems[1].checked = parentGroupColumn?.visible || false;
        this.parentVisible = parentGroupColumn?.visible || false;
    }

    metricValueFormatter(params) {
        if (params.data[params.column.colId] === null) return "";
        return params.data[params.column.colId].MetricDisplayValue;
    }

    // returns the class list that will show on each cell
    getCellClass(e) {
        const currentMetric = e.data[e.colDef.field];
        if (currentMetric == null) { // no data for this cell
            return;
        }
        const cellClasses = [];

        // because we're applying this class, agGrid no longer applies the ag-numeric-cell to numericColumns, so we have to apply it manually
        if (e.colDef.type === 'numericColumn') {
            cellClasses.push('ag-numeric-cell');
        }
        return cellClasses;
    };

    // updates the column definitions based on current data (only updates if columns are different)
    // also updates the footer because the totals are stored in the columns
    updateColumnDefs() {
        const columnDefs = [];
        const totalFooterRow = {
            ID: 'total',
            Group: {
                groupID: 'total',
                groupName: 'Totals',
            },
        };
        const avgFooterRow = {
            ID: 'avg',
            Group: {
                groupID: 'avg',
                groupName: 'Averages',
            },
        };

        // tracking variables for seeing if we need to update columns
        let numMatchedDefs = 0;
        let newColumnFound = false;
        const oldColumnDefMarker = {};
        this.gridOptions.columnDefs.forEach((colDef: ColDef) => {
            oldColumnDefMarker[colDef.field] = true;
        });

        // Parent
        if (!this.data.columnDefs.some(itm => itm.field === 'parentGroupData')) {
            this.data.columnDefs.splice(1, 0, {
                field: 'parentGroupData',
                displayName: "Parent",
                name: "",
                customName: "",
                metricID: 0,
                width: "",
                minWidth: "",
                type: "",
                enablePinning: false,
                enableHiding: false,
                footerCellTemplate: "",
                totalValue: 0,
                total: "",
                avgValue: 0,
                avg: "",
                numericType: 0
            });
        }

        // go through the returned column definitions and turn them into an object for agGrid
        const metricsByField = {};
        let index = 0;
        for (const column of this.data.columnDefs) {
            const ugColDef = column;
            let agColDef;
            if (ugColDef.displayName === "Group") {
                agColDef = {
                    headerName: ugColDef.displayName,
                    headerComponent: 'groupHeaderComponent',
                    field: ugColDef.name,
                    filter: 'agTextColumnFilter',
                    cellRenderer: 'groupRenderer',
                    pinnedRowCellRenderer: this.agGridHelperService.basicCellRenderer,
                    type: 'groupColumn',
                    pinned: "left",
                };
            } else if (ugColDef.displayName === "Parent") { // Location NOW called Parent
                agColDef = {
                    headerName: this.getLocaleText('Parent_Label'),
                    field: "parentGroupData",
                    filter: 'agTextColumnFilter',
                    hide: !this.parentVisible,
                    type: "groupColumn",
                    pinnedRowCellRenderer: () => "",
                    valueFormatter: (params: ValueFormatterParams) => {
                        if (params?.node?.data?.ID === "total") {
                            return "";
                        }

                        return params?.node?.data?.parentGroupData?.groupName
                            ? params.node.data.parentGroupData.groupName
                            : this.getLocaleText('No_Parent');
                    },
                };
            } else if (ugColDef.name.startsWith('AAgent')) {
                agColDef = {
                    headerName: ugColDef.displayName,
                    field: ugColDef.name,
                    hide: !this.agentInfoVisible
                };
            } else if (ugColDef.name.startsWith('UAttribute')) {
                const isRenamed = ugColDef.customName && ugColDef.customName !== "";
                agColDef = {
                    headerName: isRenamed ? ugColDef.customName + "*" : ugColDef.displayName,
                    field: ugColDef.name,
                    type: 'ellipsesColumn',
                };
            } else {
                const isRenamed = ugColDef.customName && ugColDef.customName !== "";
                agColDef = {
                    headerName: isRenamed ? ugColDef.customName + "*" : ugColDef.displayName,
                    headerTooltip: isRenamed ?
                        this.cvLocalizationService.getCachedText(
                            "Common", "Original_Metric", "Original Metric Name") + ": " + "\n" + ugColDef.displayName : null,
                    field: ugColDef.name,
                    comparator: this.agGridHelperService.metricNullDataComparator,
                };
            }

            // check if this column was in our column defs before
            if (oldColumnDefMarker[agColDef.field] === true) {
                numMatchedDefs++;
            } else {
                newColumnFound = true;
            }

            // add the metric specific functions for handling MetricDisplayValue and MetricValue as well as performance objectives
            const isMetric = ugColDef.metricID > 0;
            if (isMetric) {
                // so objectives know if threshold has been set in ag-grid-helper-service-base. sets MetricData.hasThreshold which is accessed in metricObjectiveStyle
                this.setHasThreshold(ugColDef, index);

                agColDef.type = 'metricColumn';
                agColDef.cellRenderer = 'metricMenuRenderer';
                agColDef.cellRendererParams = {
                    isCXOne: this.config.isCxOne,
                    groupType: this.data.rows[0]?.group?.groupType,
                    groupLevel: this.data.rows[0]?.group?.groupLevel,
                };
                agColDef.cellClassRules = {
                    'dot-green': params => this.getDirection(params.value?.MetricID) === 1 ?
                        this.isValueInThreshold(params, 0) : this.isValueInThreshold(params, 2),
                    'dot-yellow': params => this.isValueInThreshold(params, 1),
                    'dot-red': params => this.getDirection(params.value?.MetricID) === 1 ?
                        this.isValueInThreshold(params, 2) : this.isValueInThreshold(params, 0),
                };
                metricsByField[agColDef.field] = {
                    MetricID: ugColDef.metricID,
                    NumericType: ugColDef.numericType,
                    TimeType: ugColDef.timeType,
                };

                // add this columns total to footer
                totalFooterRow[agColDef.field] = {
                    value: ugColDef.totalValue,
                    MetricID: ugColDef.metricID,
                    MetricValue: ugColDef.totalValue,
                    MetricDisplayValue: ugColDef.total,
                };
                avgFooterRow[agColDef.field] = {
                    value: ugColDef.avgValue,
                    MetricID: ugColDef.metricID,
                    MetricValue: ugColDef.avgValue,
                    MetricDisplayValue: ugColDef.avg,
                };

            } else if (ugColDef.field === 'parentGroupData') { // Parent
                totalFooterRow[agColDef.field] = "";
                avgFooterRow[agColDef.field] = "";
            } else { // not a metric (group or attribute)
                // add the text from total into this footer row
                totalFooterRow[agColDef.field] = { groupName: this.isCXOne ? this.getLocaleText('Total') : this.getLocaleText('Totals'), groupID: "total" };
                avgFooterRow[agColDef.field] = { groupName: this.getLocaleText('Averages'), groupID: "avg" };
            }
            agColDef.suppressMenu = this.isSharedViaURL;

            columnDefs.push(agColDef);
            index++;
        }
        this.gridOptions.context.getMetric = params => metricsByField[params.colDef.field];

        // update footer data
        this.footerRow = totalFooterRow;
        this.avgFooterRow = [];
        if (this.isCXOne && this.showFooter || !this.isCXOne && this.showTotal) { // only put on the grid
            if (this.agGrid?.api) {
                this.agGrid?.api.setGridOption('pinnedBottomRowData', [totalFooterRow]);
            } else {
                this.gridOptions.pinnedBottomRowData = [totalFooterRow];
            }
        }
        else if (!this.isCXOne && !this.showTotal) {
            if (this.agGrid?.api)
                this.agGrid?.api.setGridOption('pinnedBottomRowData', []);
            else
                this.gridOptions.pinnedBottomRowData = [];
        }

        // update the column defs on the grid only if we need to
        let columnOrderChange = 0;
        if (columnDefs.length === this.gridOptions.columnDefs.length) {
            for (let j = 2; j < this.gridOptions.columnDefs.length; j++) {
                if (columnDefs[j].headerName !== this.gridOptions.columnDefs[j].headerName) {
                    columnOrderChange++;
                }
            }
        }

        let newMetricAdded = false;
        if (this.metrics?.toString() !== this.moduleManager.moduleSettings.selectedMetrics?.toString()) {
            this.metrics = this.moduleManager.moduleSettings.selectedMetrics;
            newMetricAdded = true;
        }

        if (newColumnFound || numMatchedDefs < this.gridOptions.columnDefs.length || columnOrderChange > 0 || newMetricAdded) {
            this.gridOptions.columnDefs = columnDefs;
            if (this.agGrid?.api) {
                this.agGrid?.api.setGridOption('columnDefs', this.gridOptions.columnDefs);
                if (this.tableState?.filterStates) {
                    this.agGrid?.api.setFilterModel(this.tableState.filterStates);
                }
                if (this.tableState) {
                    this.agGridHelperService.sizeColumnsFromApisSmart(this.agGrid?.api);
                    this.agGridHelperService.loadGridState(this.agGrid.api, this.tableState);
                    this.tableStateChanged();
                }
            }
            return true;
        }
        return false;
    }

    setHasThreshold(colDef, index) {
        const threshold = this.getThreshold(colDef.metricID);
        const hasThreshold = threshold?.find(part => part.enabled)?.enabled ?? false;
        this.data.rows.forEach(row => {
            // table has 3 columns but one is hidden in case of one metric selected so metric column will be last index which is 1 even though there are 3 coldefs
            const md = row.metricData[index - 1];
            if (md) {
                md.hasThreshold = hasThreshold;
            }
        });
    }

    async queueLaunchCoaching(params): Promise<void> {
        const baseUrl = this.navigationService.cxoneTokenUrls?.niceBaseUrl;
        const metricName = params.column.colDef.headerName;
        await this.moduleHelperService.launchCoachingClicked(baseUrl, params.node.data.Group.groupID, metricName);
    }

    getContextMenuItem(params) {
        if (typeof params.value === 'string') {
            return this.agGridHelperService.getContextMenuItems(params);
        }
        if (this.config.isCxOne) {
            return [{
                name: 'Launch Coaching',
                action: () => this.queueLaunchCoaching(params),
                disabled: false,
                icon: `<svg class="icon-16 fill-actionCellIcon">
                            <use href="../App_Themes/CXone/icons/widgets/launch-coaching.svg#launch-coaching"></use>
                        </svg>` ,
            }];
        } else {
            return [{
                name: this.cvLocalizationService.getCachedText("Objectives", "Objectives_Details", "Objectives Details"),
                action: () =>
                    this.objectivesDetailsService.openObjectivesDetailsModal(
                        params.value,
                        params.node.data.Group,
                        params.column.colDef.headerName,
                    ),
                icon: `<svg class="icon-16" style="fill: #6a7480;">
                            <use href="../App_Themes/clearview-default/icons/dashboard/gauge-range.svg#gauge-range"></use>
                       </svg>`,
            }];
        }
    }


    getThreshold(metricID: number) {
        if (this.metricThresholds == null) return null;
        return this.metricThresholds[metricID];
    }

    getDirection(metricID: number) {
        if (!this.metrics) return null;
        return this.metrics.find(m => m.Id === metricID)?.Direction;
    }

    getMetricType(metricID: number) {
        return this.metrics.find(m => m.Id === metricID)?.ConversionType;
    }

    isValueInThreshold(params: any, index: number) {
        if (params.data.ID === 'total') return false;
        const threshold = this.getThreshold(params.value?.MetricID);
        if (threshold == null) return false;
        if (threshold[index].enabled === false) return false;

        let metricValue = params.value?.MetricValue;
        if (!metricValue && metricValue !== 0) {
            return false;
        }

        let leftValue = threshold[index].left ?? 0;
        let rightValue = threshold[index].right ?? 0;
        const isTime = (leftValue + '').includes(':') || (rightValue + '').includes(':');
        const isPercent = params.value?.MetricDisplayValue.includes('%');

        if (isTime) {
            leftValue = this.getSeconds(leftValue + '');
            rightValue = this.getSeconds(rightValue + '');
            metricValue = Math.floor(metricValue);
        }

        if (isPercent) {
            metricValue = (metricValue).toFixed(2); //Make sure percent values have only two decimals.
            metricValue = Number(metricValue); //Convert value to number.
        }

        if (!isPercent && !isTime) {
            metricValue = Math.floor(metricValue);
        }

        switch (index) {
            case 0:
                return (metricValue >= leftValue);

            case 1:
                return (metricValue >= leftValue && metricValue <= rightValue);

            case 2:
                return (metricValue <= rightValue);

            default:
                return false;
        }
    }

    /** converts a time string to seconds */
    getSeconds(value: string) {
        let seconds = 0;
        const segments = value.split(':');
        for (let i = 0; i <= segments.length; i++) {
            const parsed = parseInt(segments[i], 10);
            if (Number.isNaN(parsed))
                seconds += 0;
            else
                seconds += parsed * Math.pow(60, segments.length - (i + 1));
        }
        return seconds;
    }

    /** Set visibility of a column when the user toggles a column in columns menu */
    toggleColumnVisible(i) {
        //Getting the column definition
        const column = this.agGrid?.api['columnModel'].gridColumns[i];
        const isHidden = !column.visible;
        //Hiding or showing the column
        if (column.colDef.field === "parentGroupData") {
            this.toggleParentVisible();
            return;
        } else if (column.colDef.field === "AAgent ID") {
            this.toggleAgentIdColumn();
            return;
        } else if (column.colDef.field === "AAgent Email") {
            this.toggleAgentEmailColumn();
            return;
        } else {
            this.agGrid?.api.setColumnVisible(column.colId, isHidden);
        }
        //Updating the grid
        this.tableStateChanged();
    };

    applyFilter() {
        if (this.agGrid?.api) { // notify filter change
            this.agGrid?.api.setGridOption('quickFilterText', this.filterValue.trim());
            if(this.searchOpen && this.agGrid?.api?.getDisplayedRowCount() === 0 && this.filterValue.length !== 0){
                this.agGrid?.api?.showNoRowsOverlay();
            } else {
                this.agGrid?.api?.hideOverlay();
            }
        }
    };

    clearFilter() {
        this.filterValue = "";

        if (this.agGrid?.api) { // notify filter change
            this.agGrid?.api.setGridOption('quickFilterText', this.filterValue);
            this.applyFilter();
        }
    };

    colorObjectives(row, col, metricData) {
        if (metricData.Direction === 0) { // Direction DOWN
            if (metricData.MetricValue >= metricData.AlertThreshold) return "performanceObjectiveRed";
            else if (metricData.MetricValue <= metricData.Value) return "performanceObjectiveGreen";
            else return "performanceObjectiveYellow";
        } else { // Direction UP
            if (metricData.MetricValue <= metricData.AlertThreshold) return "performanceObjectiveRed";
            else if (metricData.MetricValue >= metricData.Value) return "performanceObjectiveGreen";
            else return "performanceObjectiveYellow";
        }
    };

    toggleOpenSearch() {
        if (this.searchOpen) {
            this.searchOpen = false;
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', true);
            this.clearFilter();
        } else {
            this.searchOpen = true;
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', false);
            this.focusOnSearch();
        }
    };

    /**
     * Change input focus to the search bar.
     */
    async focusOnSearch() {
        // The element won't be ready immediately, so the focus has to be done asynchronously
        setTimeout(() => {
            if (this.config.isCxOne) {
                const el = document.querySelector(`div.cxone-text-input input[name='cxoneInput-${this.moduleKey}']`);
                $(el).focus();
            } else {
                this.searchBox?.nativeElement?.focus();
            }
        }, 200);
    }

    /**
     * Combines state saves within a second of each other into one save
     */
    queueStateSave() {
        if (this.stateRestoreInProgress)
            return;

        if (this.stateSaveTimer !== false) {
            clearTimeout(this.stateSaveTimer);
        }
        this.stateSaveTimer = setTimeout(async () => {
            this.stateSaveTimer = false;
            if (await this.moduleManager.saveState()) {
                this.moduleManager.requestPublishNeeded();
            }
        }, 500);
    };

    tableStateChanged() {
        this.tableState = this.agGridHelperService.getGridState(this.agGrid.api);
        this.queueStateSave();
    }

    toggleFooterVisible() {
        this.showFooter = !this.showFooter;
        this.setFooterVisible(this.showFooter, true);
    };

    setFooterVisible(visible, save = false) {
        const footerToShow = [];
        if (this.isCXOne) {
            this.showFooter = visible;
            if (visible)
                footerToShow.push(this.footerRow);
        }
        else {
            this.showTotal = visible;
            footerToShow.push(this.showTotal ? this.footerRow : []); 

        }

        if (this.agGrid?.api) {
            this.agGrid?.api.setGridOption('pinnedBottomRowData',footerToShow);
        } else {
            this.gridOptions.pinnedBottomRowData = footerToShow;
        }

        // setPinnedBottomRowData was reverting groupType and groupLevel so im ensuring its set correctly here
        if (this.data?.rows?.length > 0) {
            this.gridOptions.columnDefs.forEach((col: any) => {
                if (col.type === 'metricColumn') {
                    col.cellRendererParams = {isCXOne: this.config.isCxOne,
                        groupType: this.data.rows[0].group.groupType,
                        groupLevel: this.data.rows[0].group.groupLevel,
                    };
                }
            });
        }
        else {
            this.agGrid?.api?.setGridOption('rowData', []);
        }

        this.agGrid?.api?.setGridOption('columnDefs', this.gridOptions.columnDefs);

        if (save) {
            this.moduleManager.saveState('footerVisible');
            this.moduleManager.requestPublishNeeded();
        }
    };

    toggleParentVisible(skipSave = false) {
        this.parentVisible = !this.parentVisible;

        for (const checkItem of (this.isCXOne ? this.displayCheckItems : this.tableDisplayItems)) {
            if (checkItem.displayName === this.getLocaleText('Show_Parent_Level')) {
                checkItem.checked = this.parentVisible;
                break;
            }
        }

        for (const column of this.gridOptions.columnDefs) {
            const columnDefinition: ColDef = column;
            if (columnDefinition.field === 'parentGroupData') {
                columnDefinition.hide = !this.parentVisible;
                this.agGrid?.api.setColumnVisible('parentGroupData', this.parentVisible);
                if (!skipSave && this.gridReady) {
                    this.tableStateChanged();
                }
                return;
            }
        }
    };

    toggleAgentInfoVisible() {
        this.agentInfoVisible = false;

        for (const column of this.gridOptions.columnDefs) {
            const columnDefinition: ColDef = column;
            if (columnDefinition.field.startsWith('AAgent') && !columnDefinition.hide) {
                this.agentInfoVisible = true;
                break;
            }
        }

        for (const checkItem of (this.isCXOne ? this.displayCheckItems : this.tableDisplayItems)) {
            if (checkItem.displayName === 'Show Agent Info') {
                checkItem.checked = this.agentInfoVisible;
                return;
            }
        }
    };

    toggleAgentIdColumn() {
        for (const column of this.gridOptions.columnDefs) {
            const columnDefinition: ColDef = column;
            if (columnDefinition.field === 'AAgent ID') {
                columnDefinition.hide = !columnDefinition.hide;
                this.agGrid?.api.moveColumn('AAgent ID', 1);
                this.agGrid?.api.setColumnVisible('AAgent ID', !columnDefinition.hide);
                this.toggleAgentInfoVisible();
                if (this.gridReady) {
                    this.tableStateChanged();
                }
                return;
            }
        }
    }

    toggleAgentEmailColumn() {
        for (const column of this.gridOptions.columnDefs) {
            const columnDefinition: ColDef = column;
            if (columnDefinition.field === 'AAgent Email') {
                columnDefinition.hide = !columnDefinition.hide;
                this.agGrid?.api.moveColumn('AAgent Email', 2);
                this.agGrid?.api.setColumnVisible('AAgent Email', !columnDefinition.hide);
                this.toggleAgentInfoVisible();
                if (this.gridReady) {
                    this.tableStateChanged();
                }
                return;
            }
        }
    }

    toggleBothAgentColumns() {
        this.agentInfoVisible = !this.agentInfoVisible;

        for (const column of this.gridOptions.columnDefs) {
            const columnDefinition: ColDef = column;
            if (columnDefinition.field === 'AAgent ID') {
                columnDefinition.hide = !this.agentInfoVisible;
                this.agGrid?.api.moveColumn('AAgent ID', 1);
                this.agGrid?.api.setColumnVisible('AAgent ID', this.agentInfoVisible);
            }
            if (columnDefinition.field === 'AAgent Email') {
                columnDefinition.hide = !this.agentInfoVisible;
                this.agGrid?.api.moveColumn('AAgent Email', 2);
                this.agGrid?.api.setColumnVisible('AAgent Email', this.agentInfoVisible);
            }
        }
        this.toggleAgentInfoVisible();
        if (this.gridReady) {
            this.tableStateChanged();
        }
    }

    /**
     * Sorts the metric data in ascending order.
     * 
     * This function takes an array of data rows and an index for the metric data to be used for sorting.
     * It sorts the data rows based on the specified metric data in ascending order.
     * 
     * @param {PerformanceModuleDataRow[]} rows - The array of data rows to be sorted.
     * @param {number} metricDataIndex - The index of the metric data to be used for sorting.
     * @returns {any[]} - The sorted array of data rows in ascending order.
    */
    public sortMetricDataAscending(rows: PerformanceModuleDataRow[], metricDataIndex: number) {
        return rows.sort((a, b) => {
            const valueA = a.metricData[metricDataIndex]?.MetricValue ?? 0;
            const valueB = b.metricData[metricDataIndex]?.MetricValue ?? 0;
            return valueA - valueB;
        });
    }

    /**
     * Sorts the metric data in descending order.
     * 
     * This function takes an array of data rows and an index for the metric data to be used for sorting.
     * It sorts the data rows based on the specified metric data in descending order.
     * 
     * @param {PerformanceModuleDataRow[]} rows - The array of data rows to be sorted.
     * @param {number} metricDataIndex - The index of the metric data to be used for sorting.
     * @returns {any[]} - The sorted array of data rows in descending order.
    */
    public sortMetricDataDescending(rows: PerformanceModuleDataRow[], metricDataIndex: number) {
        return rows.sort((a, b) => {
            const valueA = a.metricData[metricDataIndex]?.MetricValue ?? 0;
            const valueB = b.metricData[metricDataIndex]?.MetricValue ?? 0;
            return valueB - valueA;
        });
    }

    /**
     * Sorts the group data in ascending order.
     * 
     * This function takes an array of data rows and sorts them based on the group data
     * in ascending order. It compares the group names and arranges them alphabetically.
     * 
     * @param {PerformanceModuleDataRow[]} rows - The array of data rows to be sorted.
     * @returns {any[]} - The sorted array of data rows in ascending order.
    */
    public sortGroupDataAscending(rows: PerformanceModuleDataRow[]) {
        // Step 1: Sort the rows using a custom comparator function
        rows.sort((a, b) => {
            const nameA = a.group.groupName.toUpperCase(); // Ignore upper/lower case
            const nameB = b.group.groupName.toUpperCase(); // Ignore upper/lower case
            if (nameA < nameB) {
                return -1;
            }
            if (nameA > nameB) {
                return 1;
            }
            return 0; // Names must be equal
        });

        // Step 2: Return the sorted rows
        return rows;
    }

    /**
     * Sorts the group data in descending order.
     * 
     * This function takes an array of data rows and sorts them based on the group data
     * in descending order. It compares the group names and arranges them in reverse alphabetical order.
     * 
     * @param {PerformanceModuleDataRow[]} rows - The array of data rows to be sorted.
     * @returns {any[]} - The sorted array of data rows in descending order.
    */
    public sortGroupDataDescending(rows: PerformanceModuleDataRow[]) {
        // Step 1: Sort the rows using a custom comparator function
        rows.sort((a, b) => {
            const nameA = a.group.groupName.toUpperCase(); // Ignore upper/lower case
            const nameB = b.group.groupName.toUpperCase(); // Ignore upper/lower case
            if (nameA > nameB) {
                return -1;
            }
            if (nameA < nameB) {
                return 1;
            }
            return 0; // Names must be equal
        });

        // Step 2: Return the sorted rows
        return rows;
    }

    /**
     * Updates the chart labels and data based on the current sorting criteria.
     * 
     * This function checks the sorting criteria (metricSort or groupSort) and updates
     * the chart's data rows accordingly.
     * 
     * If no sorting criteria are specified, it defaults to sorting the group data in ascending order.
     * 
     * @returns {void}
    */
    public updateChartLabelandData() {
        if(this.metricSort){
            //here i'm getting the index for the metricData that is going to be used on the sortMetricDataAscending
            let metricDataIndex = 0;
            if(this.data.rows.length > 0){
                for (let row of this.data.rows) {
                    metricDataIndex = row.metricData.findIndex((metricData) => metricData?.MetricID === this.sortID);
                    if (metricDataIndex !== -1) {
                        break;
                    }
                }
            }

            if(this.metricSort === 'asc'){
                this.data.rows = this.sortMetricDataAscending(this.data.rows, metricDataIndex);
            } else {
                this.data.rows = this.sortMetricDataDescending(this.data.rows, metricDataIndex);
            }
        }
        else if(this.groupSort){
            if(this.groupSort === 'asc'){
                this.data.rows = this.sortGroupDataAscending(this.data.rows);
            } else {
                this.data.rows = this.sortGroupDataDescending(this.data.rows);
            }
        }
        else{
            //this is the default sort
            this.data.rows = this.sortGroupDataAscending(this.data.rows);
        }

        let xAxisId = this.moduleState.graphState.chartType === 'horizontalBar' ? 'y' : 'x';
        let colorIndex = 0;
        let preppedDatasets = [];
        let seriesTypes = [];
        let xAxisLabels = [];
        let labels = [];
        let metricIDs = [];
        let datasetMetricIDs = [];
        let preparedYAxes = {};

        // Map Labels and Metric IDs to their containers.
        // This is done outside of group instantiation to only iterate over the collection once.
        this.tooltipLegendItems.clear();
        let uncountedMetrics = 0;
        const hasGroup = this.data.columnDefs.some(c => c.displayName === "Group") ? 1 : 0;
        this.data.columnDefs.map((c, index) => {
            if ((typeof c.field === "undefined" && c.metricID > 0) || c.displayName === "Group") {
                const isRenamed = c.customName && c.customName !== "";
                labels.push(isRenamed ? c.customName + "*" : c.displayName);
                metricIDs.push(c.metricID);
                if (isRenamed)
                    this.tooltipLegendItems.set(
                        index - hasGroup - uncountedMetrics, this.cvLocalizationService.getCachedText(
                            "Common", "Original_Metric", "Original Metric Name") + ": " + "\n" + c.displayName);
            }
            else
                uncountedMetrics++;
        });

        if (!this.firstAnimateComplete) {
            labels.forEach(metric => {
                this.legendItemsHidden.set(metric, false);
            });
        }

        for (let i = 0; i < this.data.rows.length; i++) {
            xAxisLabels.push(this.data.rows[i].group.groupName);

            // Iterate over the row's metrics collection for their values. Skip [0] (start @ m = 1), as it's always null.
            for (let m = 1; m < this.data.rows[i].metricData.length; m++) {
                if (this.data.rows[i].metricTypeNames[m] === "Attribute") {
                    continue;
                }

                // Create Y-Axes
                const yAxisId = (this.data.rows[i].metricTypes[m] != null)
                    ? ''.concat('YAxisType-', this.data.rows[i].metricTypes[m].toString())
                    : 'YAxisType-1'; // Some null metric types are coming through from the endpoint. Make the assumption that the interval endpoint does and stick null types as a general-type "Number."

                // Metrics should be the same for each column in the performance data. So we only need to manage axes on the first entry.
                // This will iterate over each column (index-m) in the first row (index-i), extracting the data types. After row[0], we don't need to re-run this logic. Thus, if(i==0).
                if (i === 0) {
                    const nullCheckedMetricType = (this.data.rows[i].metricTypes[m] != null) ? this.data.rows[i].metricTypes[m] : 1;

                    // Check if a Y-Axis for this NumericType already exists, creating a new one if not.
                    if (seriesTypes.indexOf(nullCheckedMetricType) < 0) {
                        this.chartAxisShowStates[yAxisId] = 1;

                        const nullCheckedMetricTypeName = this.data.rows[i].metricTypeNames[m] !== null ?
                            this.data.rows[i].metricTypeNames[m] : 'Number';
                        seriesTypes.push(nullCheckedMetricType);

                        // Check for Type 3 (Time) metrics. The scale requires special axis-tick formatting. If detected, push an axis with the right configuration. Othewise, use the default.
                        if (this.data.rows[i].metricTypes[m] === NumericType.Time &&
                            this.data.rows[i].metricTimeTypes[m] === TimeType.Time) {
                            Object.assign(preparedYAxes, {
                                [yAxisId]:
                            {
                                id: yAxisId,
                                title: {
                                    display: true,
                                    text: nullCheckedMetricTypeName,
                                    font: {
                                        weight: 'bold',
                                    },
                                },
                                display: true, // reset in verifyYAxisDisplay()
                                position: this.determinePosition(seriesTypes.length),
                                ticks: {
                                    //"2018-01-01" is arbitrary. We just need a valid date object to allow us to convert a total-seconds value to HH:MM:SS formatting.
                                    callback: (value) => moment('2018-01-01').startOf('day')
                                        .seconds(value).format('HH:mm:ss').toString()
                                }
                            }
                            });
                        } else {
                            // Default axis config (Number, Percentage, Currency)
                            Object.assign(preparedYAxes, {
                                [yAxisId]:
                            {
                                id: yAxisId,
                                title: {
                                    display: true, // reset in verifyYAxisDisplay()
                                    text: nullCheckedMetricTypeName,
                                    font: {
                                        weight: 'bold',
                                    },
                                },
                                display: true,
                                position: this.determinePosition(seriesTypes.length),
                            }
                            });
                        }

                    } else {
                        this.chartAxisShowStates[yAxisId] += 1;
                    }
                }

                const metricVal = this.data.rows[i].metricData[m]?.MetricValue ?? 0;
                const metricDisplayVal = this.data.rows[i].metricData[m]?.MetricDisplayValue ?? "0";

                // Get the matched data type.
                const matchedDatasetIndex = datasetMetricIDs.indexOf(metricIDs[m]);

                if (matchedDatasetIndex < 0) {
                    // Add the series to be displayed
                    // For grouped bar charts, push multiple points into the same label.
                    // Skip the first, the performance endpoint puts [0]=null to help render the Performance Table.
                    datasetMetricIDs.push(metricIDs[m]);

                    preppedDatasets.push({
                        //x and y axisIDs neet to be swapped for horizontal bar charts
                        xAxisID: this.moduleState.graphState.chartType === 'horizontalBar' ? yAxisId : xAxisId,
                        yAxisID: this.moduleState.graphState.chartType === 'horizontalBar' ? xAxisId : yAxisId,
                        label: labels[m],
                        backgroundColor: this.currentTheme[colorIndex],
                        borderColor: this.currentTheme[colorIndex],
                        data: [metricVal],
                        displayValues: [metricDisplayVal],
                        dataType: (this.data.rows[i].metricTypeNames[m] != null) ? this.data.rows[i].metricTypeNames[m] : 'Number',
                        fill: this.data.rows.length > 5 ? false : 'origin',
                        hitRadius: 12,
                        hoverRadius: 6,
                        pointRadius: 2,
                        pointBorderColor: this.currentTheme[colorIndex],
                        pointBackgroundColor: this.currentTheme[colorIndex],
                        hidden: this.legendItemsHidden.get(labels[m]),
                    });
                    if (this.legendItemsHidden.get(labels[m])) { // if it has been previously hidden update the count of metrics tied to specific y-axis
                        this.chartAxisShowStates[yAxisId] -= 1;
                    }
                    // Adjust color picker stuff for next series
                    if (++colorIndex >= this.currentTheme.length) {
                        colorIndex = 0;
                    }
                } else {
                    preppedDatasets[matchedDatasetIndex].data.push(metricVal);
                    preppedDatasets[matchedDatasetIndex].displayValues.push(metricDisplayVal);
                }
            }
        }

        this.chartConfig.data.datasets = preppedDatasets;
        this.chartConfig.data.labels = xAxisLabels;
    }
}
